/**
 * Step 3: Parsing Results
 * Displays domains, URLs, and phones found during parsing
 */

import {
  Space,
  Card,
  Row,
  Col,
  Statistic,
  Alert,
  Table,
  Segmented,
  Typography,
  Button,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import {
  CheckCircleOutlined,
  WarningOutlined,
  GlobalOutlined,
  LinkOutlined,
  FileTextOutlined,
  PhoneOutlined,
  SearchOutlined,
  RocketOutlined,
  LoadingOutlined,
} from '@ant-design/icons'
import type { InputMethod, DomainWithAnalysis, ParsingResult } from '../types'

const { Text, Title } = Typography

interface ResultsStepProps {
  inputMethod: InputMethod | null
  parsingResult: ParsingResult | null
  domains: DomainWithAnalysis[]
  resultsTab: 'domains' | 'pages' | 'urls' | 'phones'
  onResultsTabChange: (tab: 'domains' | 'pages' | 'urls' | 'phones') => void
  onRetry: () => void
  analyzing?: boolean // AI analysis in progress (recovering from page refresh)
}

export default function ResultsStep({
  inputMethod,
  parsingResult,
  domains,
  resultsTab,
  onResultsTabChange,
  onRetry,
  analyzing = false,
}: ResultsStepProps) {
  const hasNoDomains = domains.length === 0
  // Don't show "no data" if AI analysis is in progress (data is loading)
  const hasNoData = !analyzing && hasNoDomains && (parsingResult?.urls_count || 0) === 0

  // Data sources
  const pureDomainsData = parsingResult?.domains || []
  const pagesData = parsingResult?.pages || parsingResult?.urls || []
  const domainsData = pureDomainsData
  const urlsData = parsingResult?.urls || []
  const phonesData = parsingResult?.phones || []

  // Columns for domains table
  const domainColumns: ColumnsType<any> = [
    {
      title: '#',
      key: 'index',
      width: 50,
      render: (_, __, index) => index + 1,
    },
    {
      title: 'Домен',
      dataIndex: 'host',
      key: 'host',
      ellipsis: true,
      render: (host: string) => (
        <Space size={6}>
          <GlobalOutlined style={{ color: '#52c41a' }} />
          <a href={`https://${host}`} target="_blank" rel="noopener noreferrer" style={{ color: '#1890ff' }}>
            {host}
          </a>
        </Space>
      ),
    },
    {
      title: 'Клики',
      dataIndex: 'total_clicks',
      key: 'total_clicks',
      width: 100,
      render: (value: number) => <Text strong style={{ color: '#1890ff' }}>{value?.toLocaleString() || 0}</Text>,
    },
    {
      title: 'Запросов',
      dataIndex: 'queries_count',
      key: 'queries_count',
      width: 100,
    },
    {
      title: 'Позиция',
      dataIndex: 'average_position',
      key: 'average_position',
      width: 100,
      render: (value: number) => value?.toFixed(1) || '—',
    },
  ]

  // Columns for URLs table
  const urlColumns: ColumnsType<any> = [
    {
      title: '#',
      key: 'index',
      width: 50,
      render: (_, __, index) => index + 1,
    },
    {
      title: 'URL',
      dataIndex: 'url',
      key: 'url',
      ellipsis: true,
      render: (url: string, record: any) => (
        <div>
          <Space size={6}>
            <LinkOutlined style={{ color: '#1890ff' }} />
            <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: '#1890ff' }}>
              {url}
            </a>
          </Space>
          {record.domain && (
            <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
              <GlobalOutlined style={{ marginRight: 4 }} />
              {record.domain}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Клики',
      dataIndex: 'total_clicks',
      key: 'total_clicks',
      width: 100,
      render: (value: number) => <Text strong style={{ color: '#1890ff' }}>{value?.toLocaleString() || 0}</Text>,
    },
    {
      title: 'Позиция',
      dataIndex: 'average_position',
      key: 'average_position',
      width: 100,
      render: (value: number) => value?.toFixed(1) || '—',
    },
  ]

  // Columns for pages table
  const pagesColumns: ColumnsType<any> = [
    {
      title: '#',
      key: 'index',
      width: 50,
      render: (_, __, index) => index + 1,
    },
    {
      title: 'Страница',
      dataIndex: 'url',
      key: 'url',
      ellipsis: true,
      render: (url: string) => (
        <Space size={6}>
          <FileTextOutlined style={{ color: '#fa8c16' }} />
          <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: '#1890ff' }}>
            {url}
          </a>
        </Space>
      ),
    },
    {
      title: 'Клики',
      dataIndex: 'total_clicks',
      key: 'total_clicks',
      width: 100,
      render: (value: number) => <Text strong style={{ color: '#1890ff' }}>{value?.toLocaleString() || 0}</Text>,
    },
    {
      title: 'Позиция',
      dataIndex: 'average_position',
      key: 'average_position',
      width: 100,
      render: (value: number) => value?.toFixed(1) || '—',
    },
  ]

  // Columns for phones table
  const phoneColumns: ColumnsType<any> = [
    {
      title: '#',
      key: 'index',
      width: 50,
      render: (_, __, index) => index + 1,
    },
    {
      title: 'Телефон',
      dataIndex: 'phone_number',
      key: 'phone_number',
      render: (phone: string) => (
        <Space size={6}>
          <PhoneOutlined style={{ color: '#1890ff' }} />
          <Text copyable>{phone}</Text>
        </Space>
      ),
    },
    {
      title: 'Источник (URL)',
      dataIndex: 'source_url',
      key: 'source_url',
      ellipsis: true,
      render: (url: string) => (
        url ? (
          <a href={url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12 }}>
            {url}
          </a>
        ) : <span style={{ color: '#999' }}>—</span>
      ),
    },
  ]

  const isBatch = (parsingResult?.total_combinations || 0) > 1
  const isLoadingAnalysis = analyzing && hasNoDomains

  // Determine card state
  const getCardStyle = () => {
    if (isLoadingAnalysis) {
      return 'linear-gradient(135deg, #1890ff 0%, #40a9ff 100%)' // blue for loading
    }
    if (hasNoData) {
      return 'linear-gradient(135deg, #faad14 0%, #fa8c16 100%)' // orange for no data
    }
    return 'linear-gradient(135deg, #52c41a 0%, #73d13d 100%)' // green for success
  }

  const getCardIcon = () => {
    if (isLoadingAnalysis) {
      return <LoadingOutlined style={{ fontSize: 32 }} spin />
    }
    if (hasNoData) {
      return <WarningOutlined style={{ fontSize: 32 }} />
    }
    return <CheckCircleOutlined style={{ fontSize: 32 }} />
  }

  const getCardTitle = () => {
    if (isLoadingAnalysis) {
      return 'Восстановление AI анализа...'
    }
    if (hasNoData) {
      return 'Обработка завершена, но данных не найдено'
    }
    return inputMethod === 'manual' ? 'Ссылки обработаны!' : 'Парсинг завершен!'
  }

  const getCardSubtitle = () => {
    if (isLoadingAnalysis) {
      return 'Загружаем данные с сервера, подождите немного...'
    }
    if (isBatch) {
      return `Обработано ${parsingResult?.total_combinations || 0} комбинаций за ${parsingResult?.duration_seconds || 0}с`
    }
    return `Выполнено за ${parsingResult?.duration_seconds || 0} секунд`
  }

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Card
        bordered={false}
        style={{
          background: getCardStyle(),
          color: 'white',
        }}
      >
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {getCardIcon()}
            <div>
              <Title level={4} style={{ margin: 0, color: 'white' }}>
                {getCardTitle()}
              </Title>
              <Text style={{ color: 'rgba(255, 255, 255, 0.85)' }}>
                {getCardSubtitle()}
              </Text>
            </div>
          </div>
        </Space>
      </Card>

      {hasNoData && (
        <Alert
          message="Данных не найдено"
          description={
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <Text>Обработка завершилась, но не найдено ни одного источника.</Text>
              {inputMethod === 'yandex' && (
                <>
                  <Text strong>Возможные причины:</Text>
                  <ul style={{ margin: '8px 0', paddingLeft: 20 }}>
                    <li>Слишком узкие критерии поиска (запросы + регионы)</li>
                    <li>Исключенные слова фильтруют все результаты</li>
                    <li>В Яндекс.Вебмастере нет данных по этим запросам</li>
                    <li>Технические проблемы с парсером (проверьте логи)</li>
                  </ul>
                </>
              )}
              <Text>Попробуйте изменить параметры и повторить попытку.</Text>
            </Space>
          }
          type="warning"
          showIcon
          icon={<WarningOutlined />}
          style={{ marginTop: 8 }}
        />
      )}

      {/* Statistics cards */}
      <Row gutter={16}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Доменов"
              value={domainsData.length}
              valueStyle={{ color: domainsData.length > 0 ? '#1890ff' : '#d9d9d9' }}
              prefix={<GlobalOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title={inputMethod === 'manual' ? 'Страниц' : 'URLs'}
              value={inputMethod === 'manual' ? pagesData.length : urlsData.length}
              valueStyle={{ color: (inputMethod === 'manual' ? pagesData.length : urlsData.length) > 0 ? '#fa8c16' : '#d9d9d9' }}
              prefix={inputMethod === 'manual' ? <FileTextOutlined /> : <LinkOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Телефонов"
              value={phonesData.length}
              valueStyle={{ color: phonesData.length > 0 ? '#722ed1' : '#d9d9d9' }}
              prefix={<PhoneOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Запросов"
              value={parsingResult?.queries_count || 0}
              valueStyle={{ color: '#faad14' }}
              prefix={<SearchOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {!hasNoData && (
        <>
          {/* Tabbed view for different source types */}
          <Card size="small">
            <Segmented
              value={resultsTab}
              onChange={(value) => onResultsTabChange(value as 'domains' | 'pages' | 'urls' | 'phones')}
              block
              options={[
                {
                  label: `Домены (${domainsData.length})`,
                  value: 'domains',
                  icon: <GlobalOutlined />,
                  disabled: domainsData.length === 0,
                },
                ...(inputMethod === 'manual' ? [{
                  label: `Страницы (${pagesData.length})`,
                  value: 'pages' as const,
                  icon: <FileTextOutlined />,
                  disabled: pagesData.length === 0,
                }] : [{
                  label: `URLs (${urlsData.length})`,
                  value: 'urls' as const,
                  icon: <LinkOutlined />,
                  disabled: urlsData.length === 0,
                }]),
                {
                  label: `Телефоны (${phonesData.length})`,
                  value: 'phones',
                  icon: <PhoneOutlined />,
                  disabled: phonesData.length === 0,
                },
              ]}
              style={{ marginBottom: 16 }}
            />

            {/* Domains table */}
            {resultsTab === 'domains' && domainsData.length > 0 && (
              <div>
                <Title level={5}>
                  <GlobalOutlined style={{ marginRight: 8, color: '#52c41a' }} />
                  Найденные домены
                </Title>
                <Table
                  dataSource={domainsData}
                  columns={domainColumns}
                  pagination={{
                    defaultPageSize: 10,
                    showSizeChanger: true,
                    pageSizeOptions: ['10', '20', '50', '100'],
                    showTotal: (total) => `Всего: ${total}`,
                  }}
                  size="small"
                  rowKey="host"
                />
              </div>
            )}

            {/* Pages table (for manual input) */}
            {resultsTab === 'pages' && pagesData.length > 0 && (
              <div>
                <Title level={5}>
                  <FileTextOutlined style={{ marginRight: 8, color: '#fa8c16' }} />
                  Найденные страницы
                </Title>
                <Table
                  dataSource={pagesData}
                  columns={pagesColumns}
                  pagination={{
                    defaultPageSize: 10,
                    showSizeChanger: true,
                    pageSizeOptions: ['10', '20', '50', '100'],
                    showTotal: (total) => `Всего: ${total}`,
                  }}
                  size="small"
                  rowKey="host"
                />
              </div>
            )}

            {/* URLs table (for Yandex parsing) */}
            {resultsTab === 'urls' && urlsData.length > 0 && (
              <div>
                <Title level={5}>
                  <LinkOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                  Найденные URLs
                </Title>
                <Table
                  dataSource={urlsData}
                  columns={urlColumns}
                  pagination={{
                    defaultPageSize: 10,
                    showSizeChanger: true,
                    pageSizeOptions: ['10', '20', '50', '100'],
                    showTotal: (total) => `Всего: ${total}`,
                  }}
                  size="small"
                  rowKey={(record) => record.full_url || record.id}
                />
              </div>
            )}

            {/* Phones table */}
            {resultsTab === 'phones' && phonesData.length > 0 && (
              <div>
                <Title level={5}>
                  <PhoneOutlined style={{ marginRight: 8, color: '#722ed1' }} />
                  Найденные телефоны
                </Title>
                <Table
                  dataSource={phonesData}
                  columns={phoneColumns}
                  pagination={{
                    defaultPageSize: 10,
                    showSizeChanger: true,
                    pageSizeOptions: ['10', '20', '50', '100'],
                    showTotal: (total) => `Всего: ${total}`,
                  }}
                  size="small"
                  rowKey={(record) => record.phone_number || record.id}
                />
              </div>
            )}
          </Card>

          <Alert
            message="Следующий шаг: AI анализ"
            description="На следующем шаге вы сможете проанализировать найденные сайты с помощью AI и выбрать подходящие источники."
            type="info"
            showIcon
            icon={<RocketOutlined />}
          />
        </>
      )}

      {hasNoData && (
        <Button
          type="primary"
          icon={<RocketOutlined />}
          onClick={onRetry}
          block
          size="large"
        >
          Запустить заново
        </Button>
      )}
    </Space>
  )
}
